/**
 * For callers that only know how many columns to draw — the detail-page and
 * invoices-list skeletons, which stand in for a grid they do not own.
 */
export const PLACEHOLDER_COLUMNS = (count: number): SkeletonColumn[] =>
  Array.from({ length: count }, (_, i) => ({ key: `col-${i}` }));

interface SkeletonColumn {
  key: string | number | symbol;
  /** Mirrors `Column.id`: the React key for a column whose `key` repeats. */
  id?: string | undefined;
  width?: number | string | undefined;
}

/**
 * Loading placeholder for `DataGrid`.
 *
 * It renders a real `<table>` that iterates the grid's own columns, the way SP's
 * DataGrid does on develop (`src/components/DataGrid/index.tsx`, the `loading`
 * branch of its `tbody`). That is the whole point: the browser's table layout
 * applies the same widths to the placeholder as to the loaded rows, so the
 * skeleton is the shape of the table it replaces.
 *
 * The previous version drew flex rows and guessed widths, and capped column 0 at
 * 40px for a selection checkbox that most grids do not have — which is why the
 * narrow bar appeared first while the narrow real column (the actions cell) is
 * last.
 */
export default function DataGridSkeleton({
  columns,
  rowCount = 4,
  selectable = false,
  showHeader = true,
}: {
  columns: SkeletonColumn[];
  rowCount: number;
  selectable?: boolean;
  showHeader?: boolean;
}) {
  const bar = 'h-4 w-full max-w-[180px] rounded bg-ds-bg-subtle';

  return (
    <table className="min-w-full">
      {showHeader && (
        <thead>
          {/* Explicit 37px, the height of the real grid's header. Left to
              padding it came out at 41px, which pushed a six-row skeleton to
              359px inside the 355px the dashboard card allows and raised a
              scrollbar on every widget while loading. */}
          <tr className="border-b border-ds-border bg-ds-bg-secondary/50" style={{ height: 37 }}>
            {selectable && <th className="w-12 border-r border-ds-border px-4 py-3" />}
            {columns.map((column, index) => (
              <th
                key={`h-${column.id ?? String(column.key)}`}
                className={`px-4 py-2 ${
                  index < columns.length - 1 ? 'border-r border-ds-border' : ''
                }`}
                style={{ width: column.width ?? 'auto' }}
              >
                <div className={bar} />
              </th>
            ))}
          </tr>
        </thead>
      )}
      <tbody className="divide-y divide-ds-border">
        {Array.from({ length: rowCount }).map((_, rowIndex) => (
          // Row height matches SP's skeleton row so the placeholder does not
          // jump when the real rows land.
          <tr key={`skeleton-${rowIndex}`} className="animate-pulse" style={{ height: 53 }}>
            {selectable && (
              <td className="border-r border-ds-border px-4 py-3">
                <div className="h-4 w-4 rounded bg-ds-bg-subtle" />
              </td>
            )}
            {columns.map((column, index) => (
              <td
                key={`skeleton-${rowIndex}-${column.id ?? String(column.key)}`}
                className={`px-4 py-3 ${
                  index < columns.length - 1 ? 'border-r border-ds-border' : ''
                }`}
                style={{ width: column.width ?? 'auto' }}
              >
                <div className={bar} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Phone-sized loading placeholder for `DataGrid`.
 *
 * The grid draws cards on phones, not rows, so the table skeleton was the wrong
 * shape: a two-column table stood in for a stack of cards and the layout jumped
 * the moment the data landed. This mirrors `DataGridMobileList`'s card — the
 * same `rounded-xl` white card on the light-gray strip, a title row, then the
 * label/value rows under a rule.
 */
export function DataGridMobileSkeleton({
  rowCount = 4,
  fieldCount = 2,
}: {
  rowCount?: number;
  fieldCount?: number;
}) {
  const bar = 'h-4 rounded bg-ds-bg-subtle';

  return (
    // No background of its own, exactly like `DataGridMobileList`: the container
    // decides the surface. Painting a gray here promised a list on a different
    // surface than the header above it, and then the loaded list arrived
    // transparent — the tone changed the moment the data landed.
    <div className="flex animate-pulse flex-col gap-3 p-3">
      {Array.from({ length: rowCount }).map((_, cardIndex) => (
        <div
          key={`skeleton-card-${cardIndex}`}
          className="rounded-xl border border-ds-border-light bg-white"
        >
          <div className="px-4 py-3">
            <div className={`${bar} w-1/2`} />
          </div>
          <div className="border-t border-ds-border-light px-4 py-3">
            {Array.from({ length: fieldCount }).map((_, fieldIndex) => (
              <div
                key={`skeleton-card-${cardIndex}-${fieldIndex}`}
                className={`flex items-center justify-between gap-4 ${fieldIndex > 0 ? 'mt-3' : ''}`}
              >
                <div className={`${bar} w-24`} />
                <div className={`${bar} w-20`} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
